import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import InputMask from "react-input-mask";
import AIBackgroundEffects from "./AIBackgroundEffects";
import PaymentStep from "./PaymentStep";
import API_CONFIG, { buildApiUrl } from "../config/api.config";
import "./SubscriptionForm.css";

const SubscriptionForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [currentStep, setCurrentStep] = useState(1);

  const [formData, setFormData] = useState({
    businessName: "",
    businessWebsite: "",
    contactName: "",
    email: "",
    phoneNumber: "",
    additionalNotes: "",
    // Bot customization fields
    botFirstMessage: "",
    knowledgeBaseUrls: [""],
    voiceType: "female",
    systemPrompt: "",
  });

  const [files, setFiles] = useState([]);
  const [userEditedFirstMessage, setUserEditedFirstMessage] = useState(false);
  const [userEditedSystemPrompt, setUserEditedSystemPrompt] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [scanningWebsite, setScanningWebsite] = useState(false);
  const [scannedUrls, setScannedUrls] = useState([]);
  const [hasScanned, setHasScanned] = useState(false);
  const [loadingNextStep, setLoadingNextStep] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);

  // Generate default values when business info changes
  useEffect(() => {
    if (formData.businessName && !userEditedFirstMessage) {
      const defaultFirstMessage = `¡Bienvenido a ${formData.businessName}! Estamos dedicados a brindar un servicio excepcional. ¿En qué puedo ayudarte hoy?`;
      setFormData((prev) => ({
        ...prev,
        botFirstMessage: defaultFirstMessage,
      }));
    }
  }, [formData.businessName, userEditedFirstMessage]);

  useEffect(() => {
    if (formData.businessName && !userEditedSystemPrompt) {
      const defaultPrompt = generateDefaultSystemPrompt(formData);
      setFormData((prev) => ({
        ...prev,
        systemPrompt: defaultPrompt,
      }));
    }
  }, [
    formData.businessName,
    formData.businessWebsite,
    formData.additionalNotes,
    userEditedSystemPrompt,
  ]);

  const generateDefaultSystemPrompt = (data) => {
    const { businessName, businessWebsite, additionalNotes } = data;
    return `# Prompt del Sistema — Agente de Voz de ${
      businessName || "[Nombre del Negocio]"
    }

## Rol y Personalidad
Eres el asistente de voz profesional de **${
      businessName || "[Nombre del Negocio]"
    }**.
Tu rol es **responder preguntas de los clientes** sobre nuestro negocio, productos, servicios, horarios, ubicación y cualquier información relevante de la empresa.
Siempre hablas **en primera persona del plural** como parte de ${
      businessName || "[Nombre del Negocio]"
    } (ej., *"Ofrecemos…", "Estamos ubicados…", "Nuestros productos incluyen…"*).

## Información Principal del Negocio
- **Nombre del Negocio:** ${businessName || "No proporcionado"}
- **Sitio Web:** ${businessWebsite || "No proporcionado"}

## Detalles del Negocio
${
  additionalNotes
    ? `### Información sobre Nosotros
${additionalNotes}

**IMPORTANTE:** La información anterior describe nuestro negocio, productos/servicios y detalles operativos. Debes usar esta información para responder las preguntas de los clientes. Esto puede incluir:
- Productos o servicios que ofrecemos
- Horarios de atención y disponibilidad
- Ubicación física o áreas de cobertura
- Precios, promociones o ofertas especiales
- Experiencia, certificaciones o especialidades
- Métodos de contacto y comunicación
- Políticas de la empresa
- Cualquier otra información relevante proporcionada`
    : "No se proporcionaron detalles adicionales del negocio."
}

## Fuente de Conocimiento
- **Fuente primaria:** Usa los detalles del negocio descritos arriba como tu conocimiento principal
- **Fuente secundaria:** Base de conocimiento conectada desde nuestro sitio web y documentos cargados
- **Todas las respuestas deben provenir de la información proporcionada o de la base de conocimiento.**
- Si no tienes información sobre algo, admítelo claramente. **Nunca inventes o asumas información.**

## Estilo de Conversación
- Sé cortés, profesional y amigable
- Mantén respuestas claras y concisas, pero completas
- Adapta tu tono al tipo de negocio (formal para servicios profesionales, más casual para tiendas minoristas, etc.)
- Si un cliente pregunta algo fuera de la información disponible, indica amablemente que no tienes esa información
- Mantén un tono conversacional natural y sé paciente
- **Habla más lento al proporcionar números de teléfono, direcciones o información importante**

## Manejo de Solicitudes Específicas
- **Para citas, reservas o pedidos:** Dirige amablemente al cliente a contactar directamente al negocio
- **Para consultas de precios:** Solo proporciona precios si están explícitamente mencionados en la información disponible
- **Para quejas o problemas:** Sugiere contactar directamente con servicio al cliente
- **Para información de contacto:** Proporciona los datos claramente${
      businessWebsite ? ` y menciona nuestro sitio web: ${businessWebsite}` : ""
    }

## Límites y Restricciones
- No recopilar datos personales de los clientes
- No hacer promesas o garantías no mencionadas en la información oficial
- No proporcionar asesoría legal, médica o financiera personalizada
- No mostrar información técnica o código
- Mantente siempre en personaje como representante de ${
      businessName || "[Nombre del Negocio]"
    }
- Sé honesto sobre las limitaciones de tu conocimiento`;
  };

  // Validation patterns
  const validationPatterns = {
    email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    phone: /^[762]\d{3}-\d{4}$/,
    website:
      /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+([\/\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/,
    businessName: /^[a-zA-Z0-9\s&.,'-]{2,100}$/,
    contactName: /^[a-zA-Z\s'-]{2,50}$/,
  };

  // Real-time field validation
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "businessName":
        if (!value.trim()) {
          error = "El nombre del negocio es obligatorio";
        } else if (!validationPatterns.businessName.test(value)) {
          error =
            "El nombre del negocio solo puede contener letras, números, espacios y puntuación común";
        } else if (value.length < 2) {
          error = "El nombre del negocio debe tener al menos 2 caracteres";
        } else if (value.length > 100) {
          error = "El nombre del negocio no puede exceder 100 caracteres";
        }
        break;

      case "businessWebsite":
        if (!value.trim()) {
          error = "El sitio web del negocio es obligatorio";
        } else if (!validationPatterns.website.test(value)) {
          error =
            "Por favor ingresa una URL válida (ej., www.ejemplo.com o https://ejemplo.com)";
        }
        break;

      case "contactName":
        if (!value.trim()) {
          error = "El nombre de contacto es obligatorio";
        } else if (!validationPatterns.contactName.test(value)) {
          error =
            "El nombre solo puede contener letras, espacios, guiones y apóstrofes";
        } else if (value.length < 2) {
          error = "El nombre debe tener al menos 2 caracteres";
        } else if (value.length > 50) {
          error = "El nombre no puede exceder 50 caracteres";
        }
        break;

      case "email":
        if (!value.trim()) {
          error = "El correo electrónico es obligatorio";
        } else if (!validationPatterns.email.test(value.toLowerCase())) {
          error =
            "Por favor ingresa un correo electrónico válido (ej., juan@ejemplo.com)";
        }
        break;

      case "phoneNumber":
        const cleanedPhone = value.replace(/\D/g, "");
        if (!value.trim()) {
          error = "El número de teléfono es obligatorio";
        } else if (cleanedPhone.length !== 8) {
          error = "El número de teléfono debe tener exactamente 8 dígitos";
        } else if (!validationPatterns.phone.test(value)) {
          error =
            "El número debe comenzar con 7, 6 o 2 y tener el formato XXXX-XXXX";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Reset scan status when business website changes
    if (name === "businessWebsite" && hasScanned) {
      setHasScanned(false);
      setScannedUrls([]);
    }

    // Validate if field has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleInputBlur = (e) => {
    const { name, value } = e.target;

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate field
    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");

    if (cleaned.length === 0) return "";
    if (cleaned.length <= 4) return cleaned;
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}`;
  };

  const handlePhoneChange = (e) => {
    const { value } = e.target;
    const formattedValue = formatPhoneNumber(value);

    setFormData((prev) => ({
      ...prev,
      phoneNumber: formattedValue,
    }));

    if (touched.phoneNumber) {
      const error = validateField("phoneNumber", formattedValue);
      setErrors((prev) => ({
        ...prev,
        phoneNumber: error,
      }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Validate file size (10MB max per file)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const validFiles = selectedFiles.filter((file) => {
      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          files: `El archivo "${file.name}" excede el límite de 10MB`,
        }));
        return false;
      }
      return true;
    });

    // Limit to 5 files
    if (validFiles.length > 5) {
      setErrors((prev) => ({
        ...prev,
        files: "Máximo 5 archivos permitidos",
      }));
      setFiles(validFiles.slice(0, 5));
    } else {
      setFiles(validFiles);
      setErrors((prev) => ({
        ...prev,
        files: "",
      }));
    }
  };

  const checkEmailExists = async (email) => {
    try {
      const response = await axios.post(
        buildApiUrl(API_CONFIG.ENDPOINTS.CHECK_EMAIL),
        { email }
      );
      return response.data.exists;
    } catch (error) {
      console.error("Email check error:", error);
      return false;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldNames = [
      "businessName",
      "businessWebsite",
      "contactName",
      "email",
      "phoneNumber",
    ];

    // Mark all fields as touched
    const allTouched = {};
    fieldNames.forEach((field) => {
      allTouched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setTouched(allTouched);
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const scanWebsite = async () => {
    if (!formData.businessWebsite) return;

    setScanningWebsite(true);
    try {
      const response = await axios.post(
        buildApiUrl(API_CONFIG.ENDPOINTS.SCAN_WEBSITE),
        { url: formData.businessWebsite }
      );

      if (response.data.success && response.data.data.urls) {
        const foundUrls = response.data.data.urls;
        // Set the found URLs as knowledge base URLs
        setFormData((prev) => ({
          ...prev,
          knowledgeBaseUrls: foundUrls.length > 0 ? foundUrls : [""],
        }));
        setScannedUrls(foundUrls);
        setHasScanned(true);
        console.log(
          `Se encontraron ${foundUrls.length} páginas en el sitio web`
        );
      }
    } catch (error) {
      console.error("Error scanning website:", error);
      // If scanning fails, just keep the main website URL
      setFormData((prev) => ({
        ...prev,
        knowledgeBaseUrls: [formData.businessWebsite],
      }));
    } finally {
      setScanningWebsite(false);
    }
  };

  const handleNextStep = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector(".error-message");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Set loading state
    setLoadingNextStep(true);

    try {
      // Scan website when moving to step 2 (only if we haven't scanned yet)
      if (!hasScanned && formData.businessWebsite) {
        await scanWebsite();
      }

      setCurrentStep(2);
    } finally {
      setLoadingNextStep(false);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };

  const addKnowledgeBaseUrl = () => {
    setFormData((prev) => ({
      ...prev,
      knowledgeBaseUrls: [...prev.knowledgeBaseUrls, ""],
    }));
  };

  const removeKnowledgeBaseUrl = (index) => {
    setFormData((prev) => ({
      ...prev,
      knowledgeBaseUrls: prev.knowledgeBaseUrls.filter((_, i) => i !== index),
    }));
  };

  const updateKnowledgeBaseUrl = (index, value) => {
    const newUrls = [...formData.knowledgeBaseUrls];
    newUrls[index] = value;
    setFormData((prev) => ({
      ...prev,
      knowledgeBaseUrls: newUrls,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentStep === 1) {
      handleNextStep();
      return;
    }

    if (currentStep === 2) {
      // Validate all fields including bot customization
      const formValid = validateForm();
      if (!formValid) {
        setCurrentStep(1);
        return;
      }

      setLoading(true);
      setErrors({});

      // Check if email exists
      const exists = await checkEmailExists(formData.email);
      if (exists) {
        setErrors({
          email:
            "Este correo electrónico ya está registrado. Por favor usa un correo diferente.",
        });
        setLoading(false);
        setCurrentStep(1);
        return;
      }

      try {
        const submitData = new FormData();

        // Clean phone number before sending (remove formatting)
        const cleanedFormData = {
          ...formData,
          phoneNumber: formData.phoneNumber.replace(/\D/g, ""),
          email: formData.email.toLowerCase().trim(),
          knowledgeBaseUrls: JSON.stringify(
            formData.knowledgeBaseUrls.filter((url) => url.trim() !== "")
          ),
        };

        // Append form data
        Object.keys(cleanedFormData).forEach((key) => {
          submitData.append(key, cleanedFormData[key]);
        });

        // Append files
        files.forEach((file) => {
          submitData.append("files", file);
        });

        const response = await axios.post(
          buildApiUrl(API_CONFIG.ENDPOINTS.CREATE_SUBSCRIPTION),
          submitData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          // Guardar el ID de suscripción y pasar al paso 3 (pago)
          setSubscriptionId(response.data.data.id);
          setCurrentStep(3);
        }
      } catch (error) {
        console.error("Submission error:", error);

        if (error.response?.data?.errors) {
          const fieldErrors = {};
          error.response.data.errors.forEach((err) => {
            fieldErrors[err.field] = err.message;
          });
          setErrors(fieldErrors);
        } else {
          setErrors({
            general:
              error.response?.data?.message ||
              "Ocurrió un error. Por favor intenta de nuevo.",
          });
        }
      } finally {
        setLoading(false);
      }
    }
  };

  // Estado para guardar la referencia del pago
  const [paymentReference, setPaymentReference] = useState(null);

  // Nueva función para crear el enlace de pago
  const handleCreatePaymentLink = async () => {
    if (!subscriptionId) {
      setErrors({
        general: "Error: No se encontró el ID de suscripción"
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        buildApiUrl("/api/payments/create-link"),
        {
          subscriptionId: subscriptionId,
          returnUrl: `${window.location.origin}/payment-success`
        }
      );

      if (response.data.success) {
        // Guardar la referencia del pago para simulación
        setPaymentReference(response.data.data.reference);

        // Abrir enlace de pago de Wompi en nueva pestaña
        window.open(response.data.data.paymentUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error("Error creando enlace de pago:", error);
      setErrors({
        general: error.response?.data?.message || "Error al crear enlace de pago"
      });
    } finally {
      setLoading(false);
    }
  };

  // Nueva función para simular pago exitoso (SOLO DESARROLLO)
  const handleSimulatePayment = async () => {
    if (!paymentReference) {
      alert("Primero crea el enlace de pago");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        buildApiUrl("/api/payments/simulate-success"),
        {
          reference: paymentReference
        }
      );

      if (response.data.success) {
        // Redirigir a página de éxito con la referencia
        window.location.href = response.data.data.redirectUrl;
      }
    } catch (error) {
      console.error("Error simulando pago:", error);
      alert(error.response?.data?.message || "Error al simular pago");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to remove file from list
  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="subscription-container">
      {/* AI Background Effects */}
      <AIBackgroundEffects step={currentStep} />

      {/* Login Button - Fixed Top Right */}
      <button
        onClick={() => navigate('/login')}
        className="floating-login-btn"
        title="Iniciar Sesión"
      >
        <span className="login-icon">👤</span>
        <span className="login-text">Iniciar Sesión</span>
      </button>

      <div className="subscription-header">
        <div className="ai-animation-container">
          <div className="floating-particles"></div>
          <div className="tech-grid"></div>
          <div className="neural-network"></div>
        </div>
        <h1 className="company-name">
          <span className="text-gradient">AI Voice</span>
          <span className="text-glow">Agent</span>
        </h1>
        <p className="tagline">
          <span className="typing-animation">
            Transforma tu negocio con IA de vanguardia
          </span>
        </p>
        <div className="tech-badges">
          <div className="badge-item">
            <span className="badge-icon">🤖</span>
            <span className="badge-text">IA Avanzada</span>
          </div>
          <div className="badge-item">
            <span className="badge-icon">🌐</span>
            <span className="badge-text">24/7 Activo</span>
          </div>
          <div className="badge-item">
            <span className="badge-icon">⚡</span>
            <span className="badge-text">Respuesta Instantánea</span>
          </div>
        </div>
      </div>

      <div className="subscription-card">
        <div className="form-header">
          <h2 className="form-title-animated">
            <span className="title-main">Crea tu Agente de Voz con IA</span>
            <span className="title-sub">Tecnología de última generación</span>
          </h2>
          <div className="price-badge-3d">
            <div className="price-glow"></div>
            <div className="price-content">
              <span className="currency">$</span>
              <span className="price-number" data-value="39">
                39
              </span>
              <span className="price-period">/mes</span>
            </div>
            <div className="price-particles"></div>
          </div>

          {/* Step Indicator */}
          <div className="step-indicator">
            <div className={`step ${currentStep >= 1 ? "active" : ""}`}>
              <span className="step-number">1</span>
              <span className="step-label">Información del Negocio</span>
            </div>
            <div className="step-line"></div>
            <div className={`step ${currentStep >= 2 ? "active" : ""}`}>
              <span className="step-number">2</span>
              <span className="step-label">Personalizar Agente</span>
            </div>
            <div className="step-line"></div>
            <div className={`step ${currentStep >= 3 ? "active" : ""}`}>
              <span className="step-number">3</span>
              <span className="step-label">Realizar Pago</span>
            </div>
          </div>

          <p className="description">
            Obtén un agente de voz IA profesional para tu negocio. Maneja
            consultas de clientes 24/7 con conversaciones inteligentes y
            naturales.
          </p>
        </div>

        {errors.general && <div className="error-banner">{errors.general}</div>}

        <form onSubmit={handleSubmit} className="subscription-form" noValidate>
          {currentStep === 1 && (
            <div className="form-step">
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="businessName">
                      Nombre del Negocio *
                      {touched.businessName && !errors.businessName && (
                        <span className="valid-indicator">✓</span>
                      )}
                    </label>
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      className={
                        errors.businessName
                          ? "error"
                          : touched.businessName && !errors.businessName
                          ? "valid"
                          : ""
                      }
                      placeholder="Ej: Restaurante La Casa, Clínica Dental Sonrisas, Tienda Tech Plus"
                      maxLength="100"
                      required
                    />
                    {errors.businessName && (
                      <span className="error-message">
                        {errors.businessName}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="businessWebsite">
                      Sitio Web del Negocio *
                      {touched.businessWebsite && !errors.businessWebsite && (
                        <span className="valid-indicator">✓</span>
                      )}
                    </label>
                    <input
                      type="text"
                      id="businessWebsite"
                      name="businessWebsite"
                      value={formData.businessWebsite}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      className={
                        errors.businessWebsite
                          ? "error"
                          : touched.businessWebsite && !errors.businessWebsite
                          ? "valid"
                          : ""
                      }
                      placeholder="https://www.ejemplo.com"
                      required
                    />
                    {errors.businessWebsite && (
                      <span className="error-message">
                        {errors.businessWebsite}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-group">
                  <label htmlFor="contactName">
                    Nombre de Contacto *
                    {touched.contactName && !errors.contactName && (
                      <span className="valid-indicator">✓</span>
                    )}
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={
                      errors.contactName
                        ? "error"
                        : touched.contactName && !errors.contactName
                        ? "valid"
                        : ""
                    }
                    placeholder="Juan Pérez"
                    maxLength="50"
                    required
                  />
                  {errors.contactName && (
                    <span className="error-message">{errors.contactName}</span>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">
                      Correo Electrónico *
                      {touched.email && !errors.email && (
                        <span className="valid-indicator">✓</span>
                      )}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      className={
                        errors.email
                          ? "error"
                          : touched.email && !errors.email
                          ? "valid"
                          : ""
                      }
                      placeholder="correo@ejemplo.com"
                      required
                      autoComplete="email"
                    />
                    {errors.email && (
                      <span className="error-message">{errors.email}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phoneNumber">
                      Número de Teléfono *
                      {touched.phoneNumber && !errors.phoneNumber && (
                        <span className="valid-indicator">✓</span>
                      )}
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handlePhoneChange}
                      onBlur={handleInputBlur}
                      className={
                        errors.phoneNumber
                          ? "error"
                          : touched.phoneNumber && !errors.phoneNumber
                          ? "valid"
                          : ""
                      }
                      placeholder="7123-4567"
                      maxLength="9"
                      required
                      autoComplete="tel"
                    />
                    {errors.phoneNumber && (
                      <span className="error-message">
                        {errors.phoneNumber}
                      </span>
                    )}
                    {!errors.phoneNumber && touched.phoneNumber && (
                      <span className="help-text">
                        Formato: XXXX-XXXX, debe comenzar con 7, 6 o 2
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Materiales de Entrenamiento del Agente</h3>

                <div className="form-group">
                  <label htmlFor="files">
                    Cargar Documentos (Opcional):
                    <span className="help-text">
                      &nbsp; Carga archivos para ayudar a entrenar tu agente de
                      voz (PDF, DOC, DOCX, TXT, CSV, HTML, MD, RTF, XLS, XLSX.
                      Máx. 5 archivos, 10MB cada uno)
                    </span>
                  </label>
                  <input
                    type="file"
                    id="files"
                    name="files"
                    multiple
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt,.csv,.html,.md,.rtf,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                    className="file-input"
                  />
                  {files.length > 0 && (
                    <div className="file-list">
                      {files.map((file, index) => (
                        <div key={index} className="file-item">
                          <span className="file-name">{file.name}</span>
                          <span className="file-info">
                            <span className="file-size">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                            <button
                              type="button"
                              className="remove-file"
                              onClick={() => removeFile(index)}
                              aria-label="Eliminar archivo"
                            >
                              ×
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {errors.files && (
                    <span className="error-message">{errors.files}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="additionalNotes">
                    Detalles y Servicios del Negocio *
                    <span
                      className="help-text"
                      style={{ color: "#06c9f5", fontWeight: "500" }}
                    >
                      ⚠️ IMPORTANTE: Esta información será utilizada por tu
                      agente de voz IA para responder preguntas de los clientes
                    </span>
                  </label>
                  <textarea
                    id="additionalNotes"
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleInputChange}
                    rows="8"
                    maxLength="10000"
                    placeholder="Describe tu negocio aquí. Por ejemplo:

Somos [tipo de negocio] ubicados en [dirección]. Nuestros horarios son [horarios].

Ofrecemos los siguientes productos/servicios:
- [Producto/Servicio 1]
- [Producto/Servicio 2]
- [Producto/Servicio 3]

Nuestros precios varían desde [rango de precios]. Teléfono: [número]. Correo: [email].

[Agrega cualquier información adicional relevante sobre políticas, promociones, especialidades, etc.]"
                  />
                  <div className="character-count">
                    {formData.additionalNotes.length}/10000 caracteres
                  </div>
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "12px",
                      backgroundColor: "rgba(0, 102, 255, 0.05)",
                      borderRadius: "6px",
                      border: "1px solid #06c9f5",
                    }}
                  >
                    <strong style={{ color: "#00d4ff" }}>
                      💡 Qué incluir:
                    </strong>
                    <ul
                      style={{
                        margin: "8px 0 0 20px",
                        fontSize: "13px",
                        color: "#ffffffff",
                      }}
                    >
                      <li>Productos o servicios que ofreces</li>
                      <li>Horarios de atención (días y horas)</li>
                      <li>Ubicación física o áreas de cobertura</li>
                      <li>Información de contacto (teléfono, correo)</li>
                      <li>Precios, promociones o descuentos actuales</li>
                      <li>
                        Políticas importantes (devoluciones, garantías, envíos)
                      </li>
                      <li>Especialidades o diferenciadores de tu negocio</li>
                      <li>
                        Cualquier certificación o reconocimiento relevante
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleNextStep}
                className={`next-button ${loadingNextStep ? "loading" : ""}`}
                disabled={loadingNextStep}
                style={{
                  opacity: loadingNextStep ? 0.9 : 1,
                  cursor: loadingNextStep ? "not-allowed" : "pointer",
                }}
              >
                {loadingNextStep ? (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    <span
                      className="spinner"
                      style={{
                        width: "18px",
                        height: "18px",
                        border: "3px solid rgba(255, 255, 255, 0.3)",
                        borderTop: "3px solid #ffffff",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                        display: "inline-block",
                      }}
                    ></span>
                    <span>Analizando tu sitio web...</span>
                  </span>
                ) : (
                  "Siguiente: Personaliza Tu Agente →"
                )}
              </button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="form-step">
              <div className="form-section">
                <h3>Personalización del Agente</h3>

                <div className="form-group">
                  <label htmlFor="botFirstMessage">
                    Primer Mensaje *
                    <span className="help-text">
                      Esto es lo que dice tu agente al contestar llamadas
                    </span>
                  </label>
                  <textarea
                    id="botFirstMessage"
                    name="botFirstMessage"
                    value={formData.botFirstMessage}
                    onChange={(e) => {
                      setUserEditedFirstMessage(true);
                      handleInputChange(e);
                    }}
                    rows="3"
                    placeholder="¡Hola! Bienvenido a [Nombre del Negocio]. ¿En qué puedo ayudarte hoy?"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Tipo de Voz *</label>
                  <div className="voice-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="voiceType"
                        value="female"
                        checked={formData.voiceType === "female"}
                        onChange={handleInputChange}
                      />
                      <span className="radio-label">
                        <span className="radio-icon">👩</span>
                        Voz Femenina
                      </span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="voiceType"
                        value="male"
                        checked={formData.voiceType === "male"}
                        onChange={handleInputChange}
                      />
                      <span className="radio-label">
                        <span className="radio-icon">👨</span>
                        Voz Masculina
                      </span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    URLs de Base de Conocimiento
                    <span className="help-text">
                      {hasScanned
                        ? `✅ Se encontraron ${scannedUrls.length} páginas de tu sitio web. Puedes agregar, editar o eliminar URLs según necesites.`
                        : scanningWebsite
                        ? "🔍 Escaneando tu sitio web para encontrar todas las páginas..."
                        : "Agrega URLs del sitio web para que el agente use como fuentes de información"}
                    </span>
                  </label>
                  {scanningWebsite && (
                    <div
                      className="scanning-indicator"
                      style={{
                        padding: "15px",
                        background: "rgba(0, 102, 255, 0.05)",
                        borderRadius: "8px",
                        marginBottom: "15px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <div
                        className="spinner"
                        style={{
                          width: "20px",
                          height: "20px",
                          border: "3px solid #e0e0e0",
                          borderTop: "3px solid #4CAF50",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                      <span>Escaneando sitio web en busca de páginas...</span>
                    </div>
                  )}
                  {formData.knowledgeBaseUrls.map((url, index) => (
                    <div key={index} className="url-input-group">
                      <input
                        type="url"
                        value={url}
                        onChange={(e) =>
                          updateKnowledgeBaseUrl(index, e.target.value)
                        }
                        placeholder="https://ejemplo.com/productos, https://ejemplo.com/servicios"
                        className="url-input"
                      />
                      {formData.knowledgeBaseUrls.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeKnowledgeBaseUrl(index)}
                          className="remove-url-btn"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addKnowledgeBaseUrl}
                    className="add-url-btn"
                  >
                    + Agregar Otra URL
                  </button>
                </div>

                {/* Advanced Settings Toggle Button */}
                <div className="advanced-settings-section">
                  <button
                    type="button"
                    onClick={() =>
                      setShowAdvancedSettings(!showAdvancedSettings)
                    }
                    className="advanced-settings-toggle"
                    style={{
                      marginTop: "20px",
                      marginBottom: showAdvancedSettings ? "15px" : "0",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "20px",
                        transition: "transform 0.3s ease",
                        display: "inline-block",
                        transform: showAdvancedSettings
                          ? "rotate(90deg)"
                          : "rotate(0)",
                      }}
                    >
                      ⚙️
                    </span>
                    {showAdvancedSettings
                      ? "Ocultar Configuración Avanzada"
                      : "Mostrar Configuración Avanzada"}
                    <span
                      style={{
                        fontSize: "18px",
                        transition: "transform 0.3s ease",
                        display: "inline-block",
                        transform: showAdvancedSettings
                          ? "rotate(180deg)"
                          : "rotate(0)",
                      }}
                    >
                      ▼
                    </span>
                  </button>

                  {/* System Prompt Section - Only visible when advanced settings is open */}
                  {showAdvancedSettings && (
                    <div
                      className="form-group"
                      style={{
                        padding: "20px",
                        background: "rgba(255, 255, 255, 0.02)",
                        borderRadius: "8px",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        animation: "slideDown 0.3s ease",
                      }}
                    >
                      <label htmlFor="systemPrompt">
                        Prompt del Sistema
                        <span className="help-text">
                          Define cómo debe comportarse y responder tu agente
                          (Avanzado)
                        </span>
                      </label>
                      <textarea
                        id="systemPrompt"
                        name="systemPrompt"
                        value={formData.systemPrompt}
                        onChange={(e) => {
                          setUserEditedSystemPrompt(true);
                          handleInputChange(e);
                        }}
                        rows="12"
                        placeholder="Define la personalidad, reglas y comportamiento del agente..."
                        className="system-prompt-textarea"
                      />
                      <div className="prompt-actions">
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              systemPrompt: generateDefaultSystemPrompt(prev),
                            }))
                          }
                          className="reset-prompt-btn"
                        >
                          Restablecer a Predeterminado
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-navigation">
                <button
                  type="button"
                  onClick={handlePreviousStep}
                  className="back-button"
                >
                  ← Atrás
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading-text">Procesando...</span>
                  ) : (
                    "Continuar al Pago"
                  )}
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="form-step">
              <PaymentStep
                formData={formData}
                onPaymentCreate={handleCreatePaymentLink}
                onSimulatePayment={handleSimulatePayment}
                paymentReference={paymentReference}
                loading={loading}
              />
            </div>
          )}
        </form>

        <div className="form-footer">
          <p className="security-note">
            🔒 Tu información está segura y encriptada. Nunca compartimos tus
            datos.
          </p>
          <p className="terms">
            Al suscribirte, aceptas nuestros Términos de Servicio y Política de
            Privacidad.
          </p>
          <p className="developer-credit">
            Desarrollado con 💙 por <span className="credit-name">Rafael Martinez</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionForm;
